import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import InvoiceForm from '../../components/InvoiceForm';
import Page from '@afosto/app-components-react/Page';
import currentTime from '@afosto/utils/date/currentTime';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { invoices as invoicesApi } from '@afosto/order/api/Invoices';
import translations from './translations';

const CreateInvoice = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:create']);
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Handle creating a new invoice.
   * @param {object} client
   * @param {array} items
   * @param {array} adjustments
   * @param {object} contact
   * @param {object} organisation
   * @param {object} shippingAddress
   * @param {object} billingAddress
   * @returns {Promise<null>}
   */
  const handleCreateInvoice = async ({
    client,
    items = [],
    adjustments = [],
    contact,
    organisation,
    billingAddress,
    projection,
  }) => {
    try {
      const ruleAdjustments = (projection?.adjustments ?? [])
        .filter(adjustment => adjustment?.result?.is_applied_rule)
        .reduce(
          (adjustmentsAcc, adjustment) => [
            ...adjustmentsAcc,
            {
              reference: adjustment.reference,
              description: adjustment.description,
              amount: adjustment.amount,
              is_percentage: adjustment.is_percentage,
              is_discount: adjustment.is_discount,
            },
          ],
          [],
        );

      const invoiceItems = items.reduce((acc, item) => {
        const formattedItemAdjustments = (item?.adjustments ?? [])
          .filter(adjustment => !adjustment?.result?.is_applied_rule)
          .reduce(
            (adjustmentsAcc, adjustment) => [
              ...adjustmentsAcc,
              {
                reference: adjustment.reference,
                description: adjustment.description,
                amount: adjustment.amount,
                is_percentage: adjustment.is_percentage,
                is_discount: adjustment.is_discount,
              },
            ],
            [],
          );
        const projectionItem = findObjectInArray(
          projection?.items ?? [],
          'reference',
          item.reference,
        );
        const itemRuleAdjustments = (projectionItem?.adjustments ?? [])
          .filter(adjustment => adjustment?.result?.is_applied_rule)
          .reduce(
            (adjustmentsAcc, adjustment) => [
              ...adjustmentsAcc,
              {
                reference: adjustment.reference,
                description: adjustment.description,
                amount: adjustment.amount,
                is_percentage: adjustment.is_percentage,
                is_discount: adjustment.is_discount,
              },
            ],
            [],
          );

        return [
          ...acc,
          {
            reference: item.reference,
            sku: item.sku,
            description: item.description,
            amount: item.amount,
            is_discounted: item.is_discounted,
            quantity: item.quantity,
            adjustments: [...itemRuleAdjustments, ...formattedItemAdjustments],
          },
        ];
      }, []);

      const invoiceResponse = await invoicesApi.createInvoice({
        client_id: client?.client_id ?? null,
        contact_id: contact?.id ?? null,
        organisation_id: organisation?.id ?? null,
        address_id: billingAddress?.id ?? null,
        items: invoiceItems,
        adjustments: [...ruleAdjustments, ...(adjustments ?? [])],
        pricing_at: currentTime(),
      });
      const { data: invoice = null } = invoiceResponse;

      history.push(`/invoices/${invoice.id}`);

      return invoice;
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.invoiceNotCreated), {
        variant: 'error',
      });
      return Promise.reject(err);
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)}>
      <InvoiceForm onSubmit={handleCreateInvoice} />
    </Page>
  );
};

export default CreateInvoice;
